import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import {
  FiRefreshCw,
  FiMoreVertical,
  FiEdit2,
  FiPause,
  FiPlay,
  FiKey,
  FiSearch,
  FiClock,
  FiTrash2,
  FiDownload,
  FiMonitor,
  FiCheckCircle,
  FiAlertTriangle,
  FiDatabase,
  FiList,
  FiPlus,
  FiX,
  FiZap,
  FiGrid,
} from 'react-icons/fi';
import { useAuth } from 'shared/context/AuthContext';
import useSyncSocket from './useSyncSocket';
import SyncWizard, { InstaladorDescarga, SyncEditModal } from './SyncWizard';
import SyncHistorialModal from './SyncHistorialModal';
import SyncReplicaModal from './SyncReplicaModal';
import SyncMappingModal from './SyncMappingModal';
import {
  SS,
  SYNC_CSS,
  tipoBdLabel,
  fmtRelativo,
  fmtDuracion,
  countdown,
  estadoVisual,
  mensajeAmigable,
} from './syncUtils';

/* ── Toasts minimalistas ── */
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((mensaje, tipo = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, mensaje, tipo }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  return { toasts, push };
}

function ToastStack({ toasts }) {
  const color = { info: '#111827', success: '#059669', error: '#dc2626' };
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '22px',
        right: '22px',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '340px',
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="sync-pop"
          style={{
            background: '#fff',
            borderLeft: `4px solid ${color[t.tipo] || '#111827'}`,
            borderRadius: '10px',
            padding: '12px 16px',
            fontSize: '13px',
            color: '#111827',
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            fontWeight: 500,
          }}
        >
          {t.mensaje}
        </div>
      ))}
    </div>
  );
}

/* ── Panel de progreso en vivo (instalacion / prueba) ── */
function ProgresoInstalacion({ sede, onRegenerar, onEditar, onDesinstalar }) {
  const ag = sede.agente || {};
  const esperando = !ag.estado || ag.estado === 'pendiente_instalacion';
  const pasos = [
    { t: 'Instalar el programa', d: 'Ejecuta el instalador en el equipo del servidor.' },
    { t: 'Probar la conexión', d: 'Verificamos el acceso a tu base de datos.' },
    { t: 'Analizar tus datos', d: 'Reconocemos productos, precios y stock.' },
    { t: 'Primera sincronización', d: 'Traemos tu inventario a Agorax.' },
  ];
  const activo = esperando ? 0 : 1;
  const [menuMini, setMenuMini] = useState(false);

  return (
    <div style={{ ...SS.card, textAlign: 'center' }} className="sync-anim">
      <div style={{ position: 'relative', marginBottom: '12px' }}>
        <div
          style={{
            width: '58px',
            height: '58px',
            borderRadius: '16px',
            background: esperando ? '#fffbeb' : '#eff6ff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {esperando ? (
            <FiClock size={26} color="#d97706" />
          ) : (
            <FiRefreshCw size={26} color="#2563eb" className="sync-spin" />
          )}
        </div>
        <div style={{ position: 'absolute', top: '-4px', right: '-4px' }}>
          <button
            onClick={() => setMenuMini((v) => !v)}
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
            }}
          >
            <FiMoreVertical size={15} color="#6b7280" />
          </button>
          {menuMini && (
            <>
              <div
                onClick={() => setMenuMini(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 30 }}
              />
              <div
                className="sync-menu"
                style={{ left: 'auto', right: 0, top: '100%', marginTop: '4px' }}
              >
                <button
                  onClick={() => {
                    setMenuMini(false);
                    onEditar && onEditar();
                  }}
                  style={SS.btnGhost}
                >
                  <FiEdit2 size={15} /> Editar datos de conexión
                </button>
                <button
                  onClick={() => {
                    setMenuMini(false);
                    onRegenerar && onRegenerar();
                  }}
                  style={SS.btnGhost}
                >
                  <FiDownload size={15} /> Volver a descargar
                </button>
                <div style={{ height: '1px', background: '#f0f1f4', margin: '5px 8px' }} />
                <button
                  onClick={() => {
                    setMenuMini(false);
                    onDesinstalar && onDesinstalar();
                  }}
                  style={{ ...SS.btnGhost, color: '#dc2626' }}
                >
                  <FiTrash2 size={15} /> Desinstalar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <div style={{ fontSize: '17px', fontWeight: 700, color: '#111827' }}>
        {esperando ? 'Esperando la instalación' : 'Configurando la conexión...'}
      </div>
      <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px', marginBottom: '20px' }}>
        {esperando
          ? 'Ejecuta el instalador que descargaste en el equipo del servidor. En cuanto se conecte, verás el progreso aquí.'
          : 'Esto puede tardar un momento. No cierres esta ventana.'}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          textAlign: 'left',
          maxWidth: '440px',
          margin: '0 auto',
        }}
      >
        {pasos.map((p, i) => {
          const done = i < activo;
          const now = i === activo && !esperando;
          return (
            <div
              key={p.t}
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                padding: '12px 14px',
                background: now ? '#eff6ff' : '#f8f9fb',
                borderRadius: '12px',
                border: `1px solid ${now ? '#bfdbfe' : '#eef0f4'}`,
              }}
            >
              <div
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: done ? '#059669' : now ? '#2563eb' : '#e5e7eb',
                  color: '#fff',
                }}
              >
                {done ? (
                  <FiCheckCircle size={15} />
                ) : now ? (
                  <FiRefreshCw size={13} className="sync-spin" />
                ) : (
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#9ca3af' }}>
                    {i + 1}
                  </span>
                )}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{p.t}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>{p.d}</div>
              </div>
            </div>
          );
        })}
      </div>

      {esperando && (
        <button onClick={onRegenerar} style={{ ...SS.btnOutline, marginTop: '20px' }}>
          <FiDownload size={15} /> Volver a descargar el instalador
        </button>
      )}
    </div>
  );
}

/* ── Tarjeta de estado ── */
function CardEstado({ sede, tick }) {
  const ag = sede.agente || {};
  const vis = estadoVisual(sede);
  const cd = ag.online && ag.next_sync_at ? countdown(ag.next_sync_at) : null;
  // tick fuerza el re-render del contador
  void tick;

  return (
    <div style={SS.card}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
          }}
        >
          Estado
        </div>
        <span style={{ ...SS.chip, background: vis.bg, color: vis.color }}>
          <span
            className={vis.pulse ? 'sync-dot-pulse' : ''}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: vis.dot,
              display: 'inline-block',
            }}
          />
          {vis.label}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '46px',
            height: '46px',
            borderRadius: '13px',
            background: vis.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FiMonitor size={22} color={vis.color} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: '15px',
              fontWeight: 700,
              color: '#111827',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {ag.hostname || 'Equipo del servidor'}
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>
            {ag.online
              ? 'En línea'
              : ag.last_heartbeat
                ? `Visto ${fmtRelativo(ag.last_heartbeat)}`
                : 'Sin conexión aún'}
          </div>
        </div>
      </div>

      {cd && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 14px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <FiClock size={16} color="#059669" />
          <div style={{ fontSize: '13px', color: '#166534' }}>
            Próxima sincronización en{' '}
            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{cd}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Tarjeta de ultima sincronizacion ── */
function CardUltima({ sede }) {
  const u = sede.ultima_sync;
  return (
    <div style={SS.card}>
      <div
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#9ca3af',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          marginBottom: '16px',
        }}
      >
        Última sincronización
      </div>
      {!u ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#9ca3af' }}>
          <div
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '13px',
              background: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FiDatabase size={22} color="#9ca3af" />
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
              Todavía sin datos
            </div>
            <div style={{ fontSize: '12px' }}>Se llenará con la primera sincronización.</div>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>
              {fmtRelativo(u.fin || u.inicio)}
            </div>
            {u.estado === 'exito' ? (
              <span style={{ ...SS.chip, background: '#ecfdf5', color: '#059669' }}>
                <FiCheckCircle size={12} /> OK
              </span>
            ) : (
              <span style={{ ...SS.chip, background: '#fef2f2', color: '#dc2626' }}>
                <FiAlertTriangle size={12} /> Fallo
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '14px' }}>
            Duro {fmtDuracion(u.duracion_ms)}
          </div>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <Metric valor={u.productos} label="productos" />
            <Metric valor={u.cambios_precio} label="cambios de precio" color="#2563eb" />
            <Metric valor={u.cambios_stock} label="cambios de stock" color="#d97706" />
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ valor, label, color }) {
  return (
    <div>
      <div style={{ fontSize: '18px', fontWeight: 700, color: color || '#111827' }}>
        {valor != null ? valor : 0}
      </div>
      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{label}</div>
    </div>
  );
}

/* ── Confirmacion de desinstalacion (doble) ── */
function ConfirmDesinstalar({ sede, onClose, onConfirm }) {
  const [fase, setFase] = useState(1);
  return (
    <div style={SS.overlay} onClick={onClose}>
      <div
        className="sync-modal"
        style={{
          background: '#fff',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '440px',
          padding: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '14px',
          }}
        >
          <FiTrash2 size={24} color="#dc2626" />
        </div>
        <div style={{ fontSize: '17px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
          {fase === 1 ? 'Desinstalar la sincronización?' : 'Confirmas de verdad?'}
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px', lineHeight: 1.5 }}>
          {fase === 1
            ? 'Se detendrá la sincronización y el programa se eliminará del equipo del servidor en su próxima conexión. Los productos ya sincronizados se conservan.'
            : 'Esta acción no se puede deshacer. Tendrás que configurar todo de nuevo si quieres volver a sincronizar.'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={SS.btnOutline}>
            Cancelar
          </button>
          {fase === 1 ? (
            <button onClick={() => setFase(2)} style={{ ...SS.btnPrimary, background: '#dc2626' }}>
              Si, desinstalar
            </button>
          ) : (
            <button onClick={onConfirm} style={{ ...SS.btnPrimary, background: '#dc2626' }}>
              Desinstalar definitivamente
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════ Componente principal ══════════════ */
export default function SyncTab() {
  const { usuario } = useAuth();
  const { toasts, push } = useToasts();

  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selSede, setSelSede] = useState(null);
  const [, setTick] = useState(0);

  // Modales
  const [wizard, setWizard] = useState(false);
  const [editar, setEditar] = useState(false);
  const [mapeo, setMapeo] = useState(false);
  const [historial, setHistorial] = useState(false);
  const [replica, setReplica] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDesinst, setConfirmDesinst] = useState(false);
  const [regen, setRegen] = useState(null); // {codigo}
  const [accionando, setAccionando] = useState(false);

  const cargarPanel = useCallback(async () => {
    try {
      const res = await API.get('/sync/panel');
      if (res.data.status === 'success') {
        setPanel(res.data.data);
        setError(null);
      } else {
        setError('No se pudo cargar la información.');
      }
    } catch (e) {
      setError('No se pudo cargar la información de sincronización.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarPanel();
  }, [cargarPanel]);

  // Refresco en vivo por socket
  useSyncSocket({
    onEstado: () => cargarPanel(),
    onCompletada: (d) => {
      cargarPanel();
      const n = d && d.conteos ? d.conteos.productos : null;
      push(
        n != null ? `Sincronización completa · ${n} productos` : 'Sincronización completa',
        'success'
      );
    },
    onInventario: () => cargarPanel(),
  });

  // Contador regresivo (1s)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const sedes = panel?.sedes || [];
  const instaladorDisp = !!panel?.instalador_disponible;
  const sedeActual = sedes.find((s) => s.sede_codigo === selSede) || sedes[0] || null;

  const comando = async (cmd, { confirmMsg } = {}) => {
    if (!sedeActual) return;
    setAccionando(true);
    setMenuOpen(false);
    try {
      const res = await API.post('/sync/comando', {
        sede_codigo: sedeActual.sede_codigo,
        comando: cmd,
      });
      if (res.data.status === 'success') {
        if (confirmMsg) push(confirmMsg, 'success');
        if (cmd === 'regenerar_token' && res.data.data?.codigo_activacion) {
          setRegen({ codigo: res.data.data.codigo_activacion });
        }
        await cargarPanel();
      } else {
        push(res.data.message || 'No se pudo completar la acción.', 'error');
      }
    } catch (e) {
      push(e.response?.data?.detail || 'No se pudo completar la acción.', 'error');
    } finally {
      setAccionando(false);
    }
  };

  const descargarDesinstalador = async () => {
    if (!sedeActual) return;
    setMenuOpen(false);
    try {
      const res = await API.get(
        `/sync/descargar-desinstalador?sede_codigo=${encodeURIComponent(sedeActual.sede_codigo)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Desinstalar_AgoraxSync_${sedeActual.sede_codigo}.bat`;
      a.click();
      URL.revokeObjectURL(url);
      push('Descargamos el desinstalador. Ejecutalo en el equipo del servidor.', 'success');
    } catch (e) {
      push('No se pudo descargar el desinstalador.', 'error');
    }
  };

  /* ── Render ── */
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
        <FiRefreshCw size={26} className="sync-spin" />
        <div style={{ marginTop: '12px', fontSize: '14px' }}>Cargando sincronización...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <FiAlertTriangle size={30} color="#dc2626" />
        <div style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>{error}</div>
        <button
          onClick={() => {
            setLoading(true);
            cargarPanel();
          }}
          style={{ ...SS.btnOutline, marginTop: '16px' }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (sedes.length === 0) {
    return (
      <div
        style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af', fontSize: '14px' }}
      >
        No hay sedes disponibles para sincronizar.
      </div>
    );
  }

  const ag = sedeActual?.agente;
  const configurada = sedeActual?.configurada;
  const enProceso = ag && (ag.estado === 'pendiente_instalacion' || ag.estado === 'probando');
  const conError = ag && ag.estado === 'error';
  const pausada = ag && ag.estado === 'pausado';

  return (
    <div>
      <style>{SYNC_CSS}</style>
      <ToastStack toasts={toasts} />

      {/* Selector de sede (si hay varias) */}
      {sedes.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '18px' }}>
          {sedes.map((s) => {
            const activa = sedeActual && s.sede_codigo === sedeActual.sede_codigo;
            const sv = estadoVisual(s);
            return (
              <button
                key={s.sede_codigo}
                onClick={() => setSelSede(s.sede_codigo)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: activa ? '1.5px solid #111827' : '1.5px solid #e5e7eb',
                  background: activa ? '#111827' : '#fff',
                  color: activa ? '#fff' : '#4a4d59',
                }}
              >
                <span
                  style={{ width: '7px', height: '7px', borderRadius: '50%', background: sv.dot }}
                />
                {s.sede_nombre || s.sede_codigo}
              </button>
            );
          })}
        </div>
      )}

      {/* Estado vacio: sin configurar */}
      {!configurada ? (
        <div
          style={{ ...SS.card, textAlign: 'center', padding: '48px 28px' }}
          className="sync-anim"
        >
          <div
            style={{
              width: '68px',
              height: '68px',
              borderRadius: '18px',
              background: 'linear-gradient(135deg,#eff6ff,#ecfdf5)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
            }}
          >
            <FiRefreshCw size={30} color="#2563eb" />
          </div>
          <div style={{ fontSize: '19px', fontWeight: 700, color: '#111827' }}>
            Sincroniza tu inventario
          </div>
          <div
            style={{
              fontSize: '14px',
              color: '#6b7280',
              marginTop: '8px',
              maxWidth: '440px',
              margin: '8px auto 0',
              lineHeight: 1.55,
            }}
          >
            Conecta la base de datos de tu sistema y mantendremos tus productos, precios y stock
            siempre al dia, de forma automatica.
          </div>
          <button
            onClick={() => setWizard(true)}
            style={{ ...SS.btnPrimary, marginTop: '24px', padding: '13px 28px' }}
          >
            <FiPlus size={17} /> Configurar sincronización
          </button>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '14px' }}>
            Solo toma un par de minutos.
          </div>
        </div>
      ) : enProceso ? (
        <ProgresoInstalacion
          sede={sedeActual}
          onRegenerar={() =>
            comando('regenerar_token', { confirmMsg: 'Generamos un nuevo instalador.' })
          }
          onEditar={() => setEditar(true)}
          onDesinstalar={() => setConfirmDesinst(true)}
        />
      ) : (
        <div className="sync-anim">
          {/* Banner de error */}
          {conError && (
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                padding: '14px 16px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '14px',
                marginBottom: '16px',
              }}
            >
              <FiAlertTriangle
                size={18}
                color="#dc2626"
                style={{ flexShrink: 0, marginTop: '1px' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#991b1b' }}>
                  Hubo un problema con la conexión
                </div>
                <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '2px' }}>
                  {mensajeAmigable(ag.ultimo_error)}
                </div>
              </div>
              <button
                onClick={() => setEditar(true)}
                style={{
                  ...SS.btnOutline,
                  borderColor: '#fca5a5',
                  color: '#b91c1c',
                  flexShrink: 0,
                }}
              >
                Revisar datos
              </button>
            </div>
          )}

          {/* Banner pausada */}
          {pausada && (
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                padding: '13px 16px',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '14px',
                marginBottom: '16px',
              }}
            >
              <FiPause size={17} color="#d97706" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: '13px', color: '#92400e' }}>
                La sincronización está en pausa. Reanúdala cuando quieras.
              </div>
              <button
                onClick={() => comando('reanudar', { confirmMsg: 'Sincronización reanudada.' })}
                disabled={accionando}
                style={{ ...SS.btnPrimary, background: '#d97706', flexShrink: 0 }}
              >
                <FiPlay size={14} /> Reanudar
              </button>
            </div>
          )}

          {/* Barra de acciones */}
          <div
            className="sync-actions"
            style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}
          >
            <button
              onClick={() => {
                push('Enviando orden de sincronización...', 'info');
                comando('ejecutar_ahora', { confirmMsg: 'Sincronización en camino...' });
              }}
              disabled={accionando || pausada}
              style={{ ...SS.btnAccent, opacity: accionando || pausada ? 0.5 : 1 }}
            >
              {accionando ? <FiRefreshCw size={16} className="sync-spin" /> : <FiZap size={16} />}
              {accionando ? 'Enviando...' : 'Sincronizar ahora'}
            </button>
            <div style={{ flex: 1 }} className="sync-hide-sm" />
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                style={{ ...SS.btnOutline, padding: '10px 14px' }}
              >
                <FiMoreVertical size={17} />
              </button>
              {menuOpen && (
                <>
                  <div
                    onClick={() => setMenuOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                  />
                  <div className="sync-menu">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setEditar(true);
                      }}
                      style={SS.btnGhost}
                    >
                      <FiEdit2 size={15} /> Editar datos de conexión
                    </button>
                    {pausada ? (
                      <button
                        onClick={() =>
                          comando('reanudar', { confirmMsg: 'Sincronización reanudada.' })
                        }
                        style={SS.btnGhost}
                      >
                        <FiPlay size={15} /> Reanudar
                      </button>
                    ) : (
                      <button
                        onClick={() => comando('pausar', { confirmMsg: 'Sincronización pausada.' })}
                        style={SS.btnGhost}
                      >
                        <FiPause size={15} /> Pausar
                      </button>
                    )}
                    <button
                      onClick={() =>
                        comando('regenerar_token', { confirmMsg: 'Generamos un nuevo instalador.' })
                      }
                      style={SS.btnGhost}
                    >
                      <FiKey size={15} /> Regenerar acceso
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setMapeo(true);
                      }}
                      style={SS.btnGhost}
                    >
                      <FiGrid size={15} /> Editar mapeo de columnas
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setReplica(true);
                      }}
                      style={SS.btnGhost}
                    >
                      <FiSearch size={15} /> Explorar productos
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setHistorial(true);
                      }}
                      style={SS.btnGhost}
                    >
                      <FiList size={15} /> Ver historial
                    </button>
                    <div style={{ height: '1px', background: '#f0f1f4', margin: '5px 8px' }} />
                    <button onClick={descargarDesinstalador} style={SS.btnGhost}>
                      <FiDownload size={15} /> Descargar desinstalador
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmDesinst(true);
                      }}
                      style={{ ...SS.btnGhost, color: '#dc2626' }}
                    >
                      <FiTrash2 size={15} /> Desinstalar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tarjetas */}
          <div className="sync-grid" style={{ marginBottom: '16px' }}>
            <CardEstado sede={sedeActual} tick={0} />
            <CardUltima sede={sedeActual} />
          </div>

          {/* Resumen de conexion + accesos */}
          <div style={SS.card}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '12px',
                    background: '#f8f9fb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FiDatabase size={20} color="#6b7280" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                    {tipoBdLabel(sedeActual.config?.tipo_bd)}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {sedeActual.config?.host}
                    {sedeActual.config?.puerto ? `:${sedeActual.config.puerto}` : ''} ·{' '}
                    {sedeActual.config?.nombre_bd}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setReplica(true)} style={SS.btnOutline}>
                  <FiSearch size={15} /> Productos
                </button>
                <button onClick={() => setHistorial(true)} style={SS.btnOutline}>
                  <FiList size={15} /> Historial
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modales ── */}
      {wizard && (
        <SyncWizard
          sedes={sedes}
          sedeInicial={sedeActual?.sede_codigo}
          onClose={() => setWizard(false)}
          onConfigurado={() => {
            setWizard(false);
            cargarPanel();
            push('Configuración guardada.', 'success');
          }}
        />
      )}
      {editar && sedeActual && (
        <SyncEditModal
          sede={sedeActual}
          onClose={() => setEditar(false)}
          onGuardado={() => {
            setEditar(false);
            cargarPanel();
            push('Cambios guardados. Se aplicarán en la próxima conexión.', 'success');
          }}
        />
      )}
      {historial && sedeActual && (
        <SyncHistorialModal sede={sedeActual} onClose={() => setHistorial(false)} />
      )}
      {replica && sedeActual && (
        <SyncReplicaModal sede={sedeActual} onClose={() => setReplica(false)} />
      )}
      {mapeo && sedeActual && (
        <SyncMappingModal
          sede={sedeActual}
          onClose={() => setMapeo(false)}
          onGuardado={() => {
            setMapeo(false);
            cargarPanel();
            push(
              'Mapeo guardado. La próxima sincronización usará la nueva configuración.',
              'success'
            );
          }}
        />
      )}
      {confirmDesinst && (
        <ConfirmDesinstalar
          sede={sedeActual}
          onClose={() => setConfirmDesinst(false)}
          onConfirm={() => {
            setConfirmDesinst(false);
            comando('desinstalar', {
              confirmMsg: 'La sincronización se desinstalará en la próxima conexión.',
            });
          }}
        />
      )}
      {regen && sedeActual && (
        <div style={SS.overlay} onClick={() => setRegen(null)}>
          <div
            className="sync-modal"
            style={{
              background: '#fff',
              borderRadius: '18px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '92vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                Nuevo instalador
              </div>
              <button
                onClick={() => setRegen(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: '4px',
                  display: 'flex',
                }}
              >
                <FiX size={20} />
              </button>
            </div>
            <InstaladorDescarga
              sede={sedeActual.sede_codigo}
              codigo={regen.codigo}
              instaladorDisponible={instaladorDisp}
              onFinish={() => {
                setRegen(null);
                cargarPanel();
              }}
            />
          </div>
        </div>
      )}

      {/* firma discreta */}
      <div style={{ textAlign: 'center', fontSize: '11px', color: '#d1d5db', marginTop: '24px' }}>
        {usuario?.nombre ? `Panel de ${usuario.nombre}` : 'Sincronización Agorax'}
      </div>
    </div>
  );
}
